import { ValidateInput } from 'legacy/lut/library/validation'
import { useEffect, useState } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import { Button } from 'core-components/library/button'
import { ModalDialog } from 'core-components/library/modalDialog'
import { Controller, ValidationStatus } from 'core-components/library/validation'
import { getTranslatedText } from 'partner360/helpers/getTranslatedText'

import { checkString } from '../lib/validationHelpers'
import { ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS } from '../translations'

import styles from './AcknowledgmentActionDialog.module.scss'

type AcknowledgmentCommentDialogProps = {
  show: boolean
  mode: 'drop' | 'refuse'
  isSubmitting: boolean
  onClose: () => void
  onApply: (comment: string) => Promise<void> | void
}

type AcknowledgmentCommentDialogContentProps = {
  mode: 'drop' | 'refuse'
  comment: string
  isSubmitting: boolean
  validationStatus: ValidationStatus
  onClose: () => void
  onApply: (comment: string) => Promise<void> | void
  onCommentChange: (comment: string) => void
  onValidationStatusChange: (status: ValidationStatus) => void
}

const AcknowledgmentCommentDialogContent = ({
  mode,
  comment,
  isSubmitting,
  validationStatus,
  onClose,
  onApply,
  onCommentChange,
  onValidationStatusChange,
}: AcknowledgmentCommentDialogContentProps) => {
  const t = useSafetyTranslation()

  return (
    <div className={styles.root}>
      <p className={`${styles.description} ${styles.descriptionLeft}`}>
        {mode === 'drop' ? (
          <Translation text="jbp-commitment-manager-comment-dialog-description" />
        ) : (
          <Translation
            text={ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.commentDialog.refuseDescription}
          />
        )}
      </p>

      <Controller
        onValidationStatusChange={({ status }) => {
          if (status !== 'WIP') {
            onValidationStatusChange(status)
          }
        }}
      >
        <ValidateInput
          classes={{
            validatorClasses: {
              root: styles.input,
            },
          }}
          label={
            <Translation
              text={ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.commentDialog.commentLabel}
              span
            />
          }
          type="text"
          value={comment}
          onChange={(value: string) => onCommentChange(value)}
          checkFn={(value) =>
            checkString(
              value,
              getTranslatedText({
                t,
                key: ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.commentDialog.commentRequired,
              })
            )
          }
          field="comment"
          translation={mode === 'refuse' ? 'partnercommitment-refuse-comment' : undefined}
        />
      </Controller>

      <div className={styles.buttonLine}>
        <Button className={styles.button} color="grey" disabled={isSubmitting} onClick={onClose}>
          <Translation text="partnercommitment-cancel" span />
        </Button>
        <Button
          className={styles.button}
          color={mode === 'drop' ? 'danger' : 'warning'}
          disabled={isSubmitting || validationStatus !== ValidationStatus.Valid}
          onClick={() => void onApply(comment)}
        >
          {mode === 'drop' ? (
            <Translation text="partnercommitment-drop-sign" span />
          ) : (
            <Translation text="partnercommitment-refuse" span />
          )}
        </Button>
      </div>
    </div>
  )
}

export const AcknowledgmentCommentDialog = ({
  show,
  mode,
  isSubmitting,
  onClose,
  onApply,
}: AcknowledgmentCommentDialogProps) => {
  const [comment, setComment] = useState('')
  const [validationStatus, setValidationStatus] = useState(ValidationStatus.Invalid)

  useEffect(() => {
    if (show) {
      setComment('')
      setValidationStatus(ValidationStatus.Invalid)
    }
  }, [show, mode])

  return (
    <ModalDialog
      closeOnOutsideClick={false}
      show={show}
      size="sm"
      classes={{
        window: styles.window,
        body: styles.body,
      }}
      header={() => (
        <div className={styles.dialogHeader}>
          <div className={styles.title}>
            {mode === 'drop' ? (
              <Translation text="jbp-commitment-manager-comment-dialog-title" />
            ) : (
              <Translation
                text={ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.commentDialog.refuseTitle}
              />
            )}
          </div>
        </div>
      )}
      content={AcknowledgmentCommentDialogContent}
      contentProps={{
        mode,
        comment,
        isSubmitting,
        validationStatus,
        onClose,
        onApply,
        onCommentChange: setComment,
        onValidationStatusChange: setValidationStatus,
      }}
      onClose={onClose}
    />
  )
}
